import store from "@/state/store";

// DACORIS EvidenceReach — route table.
// Auth + the module views (Registry, Studio, Delivery, Discovery, Impact,
// Access, Funders, Admin). Each moduleRoutes tuple is
// [path, name, title, menuId, component]; a `menuId` MUST also be listed in the
// backend's MENU_IDS or the router guard silently bounces the route home.

const authGuard = (routeTo, routeFrom, next) => {
  if (store.getters["auth/loggedIn"]) next({ name: "default" });
  else next();
};

// (No `placeholder` helper any more — every module route now resolves to a real
// screen. /account/security was the last one standing.)

// Module routes — real views wired to each built area.
const moduleRoutes = [
  ["/registry/outputs/:id", "registry-output-detail", "Output", "registry.outputs",
    () => import("../views/registry/OutputDetail.vue")],
  ["/registry/podcasts", "registry-podcasts", "Podcasts", "registry.podcasts",
    () => import("../views/registry/Podcasts.vue")],
  ["/registry/taxonomy", "registry-taxonomy", "Taxonomy", "registry.taxonomy",
    () => import("../views/registry/Taxonomy.vue")],
  ["/registry/ingest", "registry-ingest", "Ingestion", "ingest.jobs",
    () => import("../views/ingest/Ingestion.vue")],
  ["/registry/review", "registry-review", "Output Review", "registry.review",
    () => import("../views/registry/OutputReview.vue")],
  ["/interop/harvest", "interop-harvest", "Repository Harvest", "interop.harvest",
    () => import("../views/interop/HarvestSources.vue")],
  ["/studio/products/:id", "studio-product-detail", "Product", "studio.products",
    () => import("../views/studio/ProductDetail.vue")],
  ["/studio/product-types", "studio-product-types", "Product Templates", "studio.product_types",
    () => import("../views/studio/ProductTypes.vue")],
  // Reuses the `studio.products` menuId deliberately: the pack is reached FROM a
  // product, never from the sidebar, so it needs no new entry in the backend's
  // MENU_IDS and cannot fail the menu-drift test.
  ["/studio/products/:id/pack", "studio-product-pack", "Publishing Pack", "studio.products",
    () => import("../views/studio/PublishingPack.vue")],
  ["/admin/users", "admin-users", "Users", "admin.users",
    () => import("../views/settings/Users.vue")],
  ["/admin/roles", "admin-roles", "Roles & Permissions", "admin.roles",
    () => import("../views/settings/Roles.vue")],
  ["/admin/settings", "admin-settings", "Settings", "admin.settings",
    () => import("../views/settings/Settings.vue")],
  ["/access/requests", "access-requests", "Controlled Access", "access.requests",
    () => import("../views/access/AccessRequests.vue")],
  ["/admin/operations", "admin-operations", "Operations", "ops.audit",
    () => import("../views/ops/OpsCenter.vue")],
  ["/admin/packs", "admin-packs", "Client Packs", "admin.packs",
    () => import("../views/admin/ClientPacks.vue")],
  ["/admin/tenants", "admin-tenants", "Institutions", "admin.tenants",
    () => import("../views/admin/Tenants.vue")],
  ["/funders/registry", "funder-registry", "Funders", "funder.registry",
    () => import("../views/funders/Funders.vue")],
  ["/funders", "funder-dashboard", "Funder Intelligence", "funder.dashboard",
    () => import("../views/funders/FunderDashboard.vue")],
  ["/funders/reports", "funder-reports", "Funder Reports", "funder.reports",
    () => import("../views/funders/FunderReports.vue")],
  ["/funders/grants", "funder-grants", "Grants & Awards", "funder.grants",
    () => import("../views/funders/Grants.vue")],
  // detail route, no sidebar entry — its menuId must STILL be in the backend's
  // MENU_IDS or the guard bounces it home with only a console.warn
  ["/funders/:id", "funder-portfolio", "Funder Portfolio", "funder.portfolio",
    () => import("../views/funders/FunderPortfolio.vue")],
  ["/impact", "impact-dashboard", "Impact & Analytics", "impact.dashboard",
    () => import("../views/impact/ImpactDashboard.vue")],
  ["/impact/cases", "impact-cases", "Impact Cases", "impact.cases",
    () => import("../views/impact/ImpactCases.vue")],
  ["/impact/cases/:id", "impact-case-detail", "Impact Case", "impact.cases",
    () => import("../views/impact/ImpactCaseDetail.vue")],
  ["/impact/validation", "impact-validation", "Impact Validation", "impact.validation",
    () => import("../views/impact/ImpactValidation.vue")],
  ["/integrations", "integrations", "Integrations", "integrations",
    () => import("../views/integrations/Integrations.vue")],
  ["/integrations/api", "integrations-api", "Data API & Webhooks", "integrations",
    () => import("../views/integrations/ApiAccess.vue")],
  ["/notifications", "notifications", "Notifications", null,
    () => import("../views/notifications/Notifications.vue")],
  ["/account/notifications", "account-notifications", "Notifications", null,
    () => import("../views/account/NotificationPrefs.vue")],
  ["/account/security", "account-security", "My Security", null,
    () => import("../views/account/Security.vue")],
].map(([path, name, title, menuId, component, props]) => ({
  path,
  name,
  component,
  ...(props ? { props } : {}),
  meta: { title, authRequired: true, ...(menuId ? { menuId } : {}) },
}));

export default [
  // --- Auth ---
  {
    path: "/login",
    name: "login",
    component: () => import("../views/account/login.vue"),
    meta: { title: "Login", beforeResolve: authGuard },
  },
  // No self-registration: DACORIS provisions users through an administrator
  // (POST /admin/users -> views/settings/Users.vue). Every account needs a role
  // AND an active tenant membership, so a self-registered user would fail closed
  // with 403 on every request. The inherited template's register view called a
  // POST /auth/register that this API has never served — removed 2026-08-08
  // rather than inventing a signup flow the governance model does not want.
  {
    path: "/forgot-password",
    name: "Forgot password",
    component: () => import("../views/account/forgot-password.vue"),
    meta: { title: "Forgot password", beforeResolve: authGuard },
  },
  {
    path: "/logout",
    name: "logout",
    meta: {
      title: "Logout",
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        localStorage.removeItem("jwt");
        localStorage.removeItem("user");
        localStorage.removeItem("userdata");
        // The Vuex menu cache is in-memory and this logout does NOT reload the
        // page, so without this the next user to log in in the same tab
        // inherits the previous user's sidebar until the 5-minute cache
        // expires. (Harmless before the sidebar was permission-filtered —
        // nothing read the cache except the guard, which refetched every time.)
        store.dispatch("menuAccess/clearMenus");
        next({ name: "login" });
      },
    },
    component: () => import("../views/account/login.vue"),
  },

  // --- App ---
  {
    path: "/",
    name: "default",
    component: () => import("../views/dashboard/LeadershipDashboard.vue"),
    meta: { title: "Dashboard", authRequired: true },
  },
  {
    path: "/discovery/search",
    name: "discovery-search",
    component: () => import("../views/search/Search.vue"),
    meta: { title: "Search", authRequired: true, menuId: "discovery.search" },
  },
  {
    path: "/dashboards/staff", name: "dashboard-staff",
    component: () => import("../views/dashboard/RoleDashboard.vue"),
    meta: { title: "My Work", authRequired: true, menuId: "dashboard.staff", dashboard: "staff" },
  },
  {
    path: "/dashboards/comms", name: "dashboard-comms",
    component: () => import("../views/dashboard/RoleDashboard.vue"),
    meta: { title: "Control Room", authRequired: true, menuId: "dashboard.comms", dashboard: "comms" },
  },
  {
    path: "/dashboards/department", name: "dashboard-department",
    component: () => import("../views/dashboard/RoleDashboard.vue"),
    meta: { title: "Department", authRequired: true, menuId: "dashboard.department", dashboard: "department" },
  },
  {
    path: "/registry/outputs",
    name: "registry-outputs",
    component: () => import("../views/registry/Outputs.vue"),
    meta: { title: "Outputs", authRequired: true, menuId: "registry.outputs" },
  },
  // Outputs by workflow stage — one component, one route + menuId per stage, so each
  // role can be given its own left-nav entries (same shape as the Products stages).
  {
    path: "/registry/outputs/mine",
    name: "registry-outputs-mine",
    component: () => import("../views/registry/Outputs.vue"),
    props: { stage: "mine" },
    meta: { title: "My Outputs", authRequired: true, menuId: "registry.outputs_mine" },
  },
  {
    path: "/registry/outputs/submitted",
    name: "registry-outputs-submitted",
    component: () => import("../views/registry/Outputs.vue"),
    props: { stage: "submitted" },
    meta: { title: "Submitted Outputs", authRequired: true, menuId: "registry.outputs_submitted" },
  },
  {
    path: "/registry/outputs/in-review",
    name: "registry-outputs-in-review",
    component: () => import("../views/registry/Outputs.vue"),
    props: { stage: "in_review" },
    meta: { title: "Outputs In Review", authRequired: true, menuId: "registry.outputs_in_review" },
  },
  {
    path: "/registry/outputs/approved",
    name: "registry-outputs-approved",
    component: () => import("../views/registry/Outputs.vue"),
    props: { stage: "approved" },
    meta: { title: "Approved Outputs", authRequired: true, menuId: "registry.outputs_approved" },
  },
  {
    path: "/registry/outputs/archived",
    name: "registry-outputs-archived",
    component: () => import("../views/registry/Outputs.vue"),
    props: { stage: "archived" },
    meta: { title: "Archived Outputs", authRequired: true, menuId: "registry.outputs_archived" },
  },
  {
    path: "/discovery/links",
    name: "discovery-links",
    component: () => import("../views/discovery/DiscoveredLinks.vue"),
    meta: { title: "Discovered Links", authRequired: true, menuId: "discovery.links" },
  },
  {
    path: "/admin/document-templates",
    name: "admin-document-templates",
    component: () => import("../views/admin/DocumentTemplates.vue"),
    meta: { title: "Document Templates", authRequired: true,
            menuId: "admin.document_templates" },
  },
  {
    path: "/studio/products",
    name: "studio-products",
    component: () => import("../views/studio/Products.vue"),
    props: { stage: "draft" },
    meta: { title: "Draft Products", authRequired: true, menuId: "studio.products" },
  },
  {
    path: "/studio/review",
    name: "studio-review",
    component: () => import("../views/studio/Products.vue"),
    props: { stage: "ready_for_review" },
    meta: { title: "Products In Review", authRequired: true, menuId: "studio.review" },
  },
  {
    path: "/studio/approved",
    name: "studio-approved",
    component: () => import("../views/studio/Products.vue"),
    props: { stage: "approved" },
    meta: { title: "Approved Products", authRequired: true, menuId: "studio.approved" },
  },
  {
    // The ONLYOFFICE editor on a page of its own, for staff who want the whole
    // screen instead of the modal. Reached from the "Open in new tab" button on
    // every embedded editor, never from the sidebar.
    //
    // NO `menuId`, deliberately: the router guard only checks menu permissions
    // when a route declares one, so this needs no matching entry in the
    // backend's MENU_IDS. Document permissions are unaffected — the editor
    // still gets its signed config from /onlyoffice/config/{kind}/{id}.
    path: "/documents/:kind/:docId",
    name: "document-editor",
    component: () => import("../views/documents/DocumentEditor.vue"),
    meta: { title: "Document", authRequired: true },
  },
  {
    path: "/delivery/publications",
    name: "delivery-publications",
    component: () => import("../views/delivery/Publications.vue"),
    meta: { title: "Publications", authRequired: true, menuId: "delivery.publications" },
  },
  ...moduleRoutes,

  // --- 404 catch-all ---
  {
    path: "/:pathMatch(.*)*",
    name: "404",
    component: () => import("../views/auth/errors/404.vue"),
    meta: { title: "Not found" },
  },
];
